import React, { useState, useRef } from 'react';
import config from '../config';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { creerAnnonce } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Upload, X, Car } from 'lucide-react';

const CreerAnnonce = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
  titre: '', marque: '', modele: '', annee: '',
  kilometrage: '', carburant: '', transmission: '',
  type_vehicule: '',
  prix: '', negociable: false, description: '', localisation: ''
  });

  if (!user) { navigate('/connexion'); return null; }

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(prev => [...prev, ...files]);
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const retirerPhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErreur('');
    try {
      const res = await creerAnnonce(form);
      const annonceId = res.data.annonce.id;

      if (photos.length > 0) {
        const formData = new FormData();
        photos.forEach(photo => formData.append('photos', photo));
        formData.append('est_principale', 'true');
        await fetch(`${config.API_URL}/api/photos/${annonceId}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: formData
        });
      }

      navigate(`/annonces/${annonceId}`);
    } catch (err) {
      setErreur(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-yellow-400 transition bg-white";
  const labelClass = "block text-xs font-semibold text-gray-500 mb-1";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div style={{ background: '#0A0A0A' }} className="py-8 px-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div style={{ background: '#F5A62320' }} className="w-10 h-10 rounded-xl flex items-center justify-center">
            <Car size={20} style={{ color: '#F5A623' }} />
          </div>
          <div>
            <h1 className="text-white text-2xl font-black">Publier une annonce</h1>
            <p className="text-gray-400 text-sm">Remplissez les informations de votre véhicule</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">

        {erreur && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-2xl mb-6 text-sm font-semibold">
            ⚠️ {erreur}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Infos véhicule */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Informations du véhicule
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Titre de l'annonce</label>
                <input type="text" required value={form.titre}
                  onChange={(e) => setForm({ ...form, titre: e.target.value })}
                  className={inputClass} placeholder="Ex: Toyota Prado 2020 très bon état"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Marque</label>
                  <input type="text" required value={form.marque}
                    onChange={(e) => setForm({ ...form, marque: e.target.value })}
                    className={inputClass} placeholder="Toyota"
                  />
                </div>
                <div>
                  <label className={labelClass}>Modèle</label>
                  <input type="text" required value={form.modele}
                    onChange={(e) => setForm({ ...form, modele: e.target.value })}
                    className={inputClass} placeholder="Prado"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Année</label>
                  <input type="number" required value={form.annee}
                    onChange={(e) => setForm({ ...form, annee: e.target.value })}
                    className={inputClass} placeholder="2020" min="1900" max="2026"
                  />
                </div>
                <div>
                  <label className={labelClass}>Kilométrage</label>
                  <input type="number" required value={form.kilometrage}
                    onChange={(e) => setForm({ ...form, kilometrage: e.target.value })}
                    className={inputClass} placeholder="45000"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Carburant</label>
                  <select required value={form.carburant}
                    onChange={(e) => setForm({ ...form, carburant: e.target.value })}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    <option value="essence">Essence</option>
                    <option value="diesel">Diesel</option>
                    <option value="electrique">Électrique</option>
                    <option value="hybride">Hybride</option>
                    <option value="gpl">GPL</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Transmission</label>
                  <select required value={form.transmission}
                    onChange={(e) => setForm({ ...form, transmission: e.target.value })}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    <option value="manuelle">Manuelle</option>
                    <option value="automatique">Automatique</option>
                  </select>
                </div>
                <div>
                <label className={labelClass}>Type de véhicule</label>
                <select value={form.type_vehicule}
                  onChange={(e) => setForm({ ...form, type_vehicule: e.target.value })}
                  className={inputClass}
                >
                  <option value="">Sélectionner</option>
                  <option value="berline">Berline</option>
                  <option value="suv">SUV</option>
                  <option value="4x4">4x4</option>
                  <option value="pickup">Pick-up</option>
                  <option value="utilitaire">Utilitaire</option>
                  <option value="engin">Engin</option>
                  <option value="citadine">Citadine</option>
                  <option value="monospace">Monospace</option>
                  <option value="moto">Moto</option>
                </select>
              </div>
              </div>
            </div>
          </div>

          {/* Prix */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Prix
            </h2>
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <label className={labelClass}>Prix (FCFA)</label>
                <input type="number" required value={form.prix}
                  onChange={(e) => setForm({ ...form, prix: e.target.value })}
                  className={inputClass} placeholder="5000000"
                />
              </div>
              <div className="flex items-center gap-2 mt-4">
                <input type="checkbox" id="negociable" checked={form.negociable}
                  onChange={(e) => setForm({ ...form, negociable: e.target.checked })}
                  className="w-4 h-4 accent-yellow-400"
                />
                <label htmlFor="negociable" className="text-sm text-gray-600 font-semibold">Négociable</label>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Description & Localisation
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Description</label>
                <textarea value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={4} className={inputClass}
                  placeholder="Décrivez votre véhicule en détail..."
                />
              </div>
              <div>
                <label className={labelClass}>Localisation</label>
                <input type="text" value={form.localisation}
                  onChange={(e) => setForm({ ...form, localisation: e.target.value })}
                  className={inputClass} placeholder="Brazzaville, Congo"
                />
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Photos & Vidéos
            </h2>

            {/* 🆕 LIMITES PHOTOS/VIDÉOS (Fix #4) */}
            <div className="flex gap-2 mb-3 flex-wrap">
              {[
                { plan: 'Plan Gratuit', photos: 5, video: false, color: '#6b7280' },
                { plan: 'Starter', photos: 10, video: false, color: '#3b82f6' },
                { plan: 'Business', photos: 10, video: true, color: '#8b5cf6' },
                { plan: 'Premium', photos: 10, video: true, color: '#F5A623' }
              ].map(p => (
                <div key={p.plan} style={{ border: `1px solid ${p.color}30`, background: `${p.color}10` }}
                  className="rounded-lg px-3 py-1 text-xs">
                  <span style={{ color: p.color }} className="font-bold">{p.plan}</span>
                  <span className="text-gray-500 ml-1">{p.photos} photos {p.video ? '+ vidéo' : ''}</span>
                </div>
              ))}
            </div>

            <div
              onClick={() => fileInputRef.current.click()}
              style={{ border: '2px dashed #e5e7eb' }}
              className="rounded-2xl p-6 text-center cursor-pointer hover:border-yellow-400 transition"
            >
              <Upload size={36} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 font-semibold text-sm">Cliquez pour ajouter des photos ou vidéos</p>
              <p className="text-xs text-gray-400 mt-1">Photos (JPG, PNG, WEBP) et Vidéos (MP4) — 50MB max par fichier</p>

              {/* Conseils vidéo */}
              <div className="mt-4 bg-blue-50 rounded-xl p-3 text-left">
                <p className="text-xs font-bold text-blue-700 mb-1">📹 Conseils pour la vidéo :</p>
                <ul className="text-xs text-blue-600 space-y-0.5">
                  <li>✅ Filmez en <strong>mode paysage</strong> (téléphone horizontal)</li>
                  <li>✅ Faites le tour complet du véhicule</li>
                  <li>✅ Durée idéale : <strong>30 à 60 secondes</strong></li>
                  <li>✅ Format recommandé : <strong>MP4, 720p ou 1080p</strong></li>
                  <li>❌ Évitez le mode portrait (téléphone vertical)</li>
                </ul>
              </div>
            </div>

            {/* ✅ FIX #1: Ajout du ref={fileInputRef} et FIX #5: Upload multi-photos */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/mp4"
              onChange={handlePhotos}
              className="hidden"
            />

            {previews.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-4">
                {previews.map((preview, index) => (
                  <div key={index} className="relative">
                    {photos[index]?.type?.startsWith('video/') ? (
                      <video src={preview} className="w-full h-32 object-cover rounded-xl" controls />
                    ) : (
                      <img src={preview} alt={`apercu ${index + 1}`} className="w-full h-32 object-cover rounded-xl" />
                    )}
                    {index === 0 && (
                      <span style={{ background: '#F5A623' }} className="absolute top-1 left-1 text-black text-xs px-2 py-0.5 rounded-full font-bold">
                        Principale
                      </span>
                    )}
                    <button type="button" onClick={() => retirerPhoto(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Boutons */}
          <div className="flex gap-4">
            <button type="button" onClick={() => navigate('/mes-annonces')}
              className="flex-1 bg-white border border-gray-200 text-gray-600 py-3 rounded-2xl font-bold hover:bg-gray-50 transition"
            >
              Annuler
            </button>
            <button type="submit" disabled={loading}
              style={{ background: '#F5A623' }}
              className="flex-1 text-black py-3 rounded-2xl font-black hover:opacity-90 transition disabled:opacity-50"
            >
              {loading ? 'Publication...' : 'Publier l\'annonce'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreerAnnonce;